import { useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useTransform, useScroll, useSpring } from "framer-motion";
import { useBreakpointValue } from "@chakra-ui/react";
import Container from "components/base/Container";
import {
  MotionBox as Box,
  MotionGrid as Grid,
  MotionGridItem as GridItem,
  MotionFlex as Flex,
} from "utils/motion";
import Button from "components/base/Button";
import { imgLoader } from "components/base/Image";
import Text from "components/base/Text";
import Trans, { useFormatMessage } from "components/base/Trans";
import CampaignPriceBlock from "components/blocks/CampaignPriceBlock";

const VideoPlayer = dynamic(() => import("components/sections/VideoPlayer"), {
  ssr: false,
});

const videoURL =
  "https://vz-49830c06-001.b-cdn.net/38f3eae2-b6af-46bf-bbdd-acf4dd57d3b3/playlist.m3u8";
const videoMobileURL =
  "https://vz-49830c06-001.b-cdn.net/38f3eae2-b6af-46bf-bbdd-acf4dd57d3b3/playlist.m3u8";
const videoBlockedImageMobile = "/images/home/header-mobile2.jpg";
const videoBlockedImageDesktop = "/images/home/header2.jpg";

const appearBelow = (delay) => ({
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay } },
});

const scaleOut = {
  scaled: { scale: 1.1 },
  normal: { scale: 1, transition: { type: "tween", duration: 6, delay: 0.4 } },
};

const Item = ({ label, value, delay, children, ...props }) => (
  <GridItem
    colSpan={[2, 2, 2, 1]}
    {...props}
    initial="hidden"
    variants={appearBelow(delay)}
    animate="visible"
  >
    {children}
    {!children && value && (
      <Flex align="center" direction="column">
        <Text
          type="headline_sm"
          as="p"
          fontSize={["20px", null, "24px", null, "32px"]}
          lineHeight={["28px", null, "32px", null, "40px"]}
          color="white"
          textTransform="lowercase"
          letterSpacing={0}
          align="center"
        >
          {value}
        </Text>
        <Text type="title_xs" as="p" color="white" align="center" mt={["xxs"]}>
          {label}
        </Text>
      </Flex>
    )}
  </GridItem>
);

export default function HomeHeroHeader({
  animationId,
  noSpecs,
  imInterestedClick,
  props,
}) {
  const formatMessage = useFormatMessage();
  const ref = useRef(null);
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: true });
  const [videoBlocked, setVideoBlocked] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "155vh"],
  });
  const springScroll = useSpring(scrollYProgress, {
    stiffness: 176,
    damping: 26,
  });
  const opacityContent = useTransform(springScroll, [0, 0.1, 0.2], [1, 1, 0]);
  const opacityImg = useTransform(springScroll, [0.1, 0.5], [1, 0]);
  const yContent = useTransform(springScroll, [0, 0.5], ["0%", "-5vh"]);
  const yImg = useTransform(springScroll, [0, 0.5], ["0%", "-15vh"]);
  const zIndexBox = useTransform(springScroll, [0.9, 1], [0, -1]);

  return (
    <Box
      ref={ref}
      position="relative"
      w="100%"
      h="100%"
      bg="black.900"
      as="section"
      {...props}
    >
      <Box
        height={["100%", null, "105vh"]}
        minHeight="750px"
        position="relative"
        overflowX="hidden"
        overflowY="hidden"
        style={{ zIndex: zIndexBox }}
      >
        <Box
          position="fixed"
          width="100%"
          height="100%"
          style={{ opacity: opacityImg, y: yImg }}
        >
          <Box height="105vh" position="relative">
            {/* Fallback image — always underneath the video so a slow first
                load shows the poster instead of a black background. It is
                only covered once the video is playing.

                The crop is chosen by the browser at parse time via <picture>
                instead of by a JS breakpoint. With useBreakpointValue the SSR
                markup always requested the mobile crop and the desktop crop
                was only fetched after hydration, which flashed on cold loads.
                The slow zoom lives on this image only: continuously scaling
                the container that also holds the <video> forces Chromium to
                re-rasterise the video layer every frame while it plays. */}
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="105vh"
              initial="scaled"
              variants={scaleOut}
              animate="normal"
              style={{ willChange: "transform" }}
            >
              <picture>
                <source
                  media="(min-width: 768px)"
                  srcSet={`${imgLoader({
                    src: videoBlockedImageDesktop,
                    width: 1920,
                  })} 1920w`}
                  sizes="100vw"
                />
                <img
                  src={imgLoader({ src: videoBlockedImageMobile, width: 1080 })}
                  srcSet={`${imgLoader({
                    src: videoBlockedImageMobile,
                    width: 640,
                  })} 640w, ${imgLoader({
                    src: videoBlockedImageMobile,
                    width: 1080,
                  })} 1080w`}
                  sizes="100vw"
                  alt="hero"
                  fetchPriority="high"
                  decoding="async"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "105vh",
                    objectFit: "cover",
                    opacity: 0.8,
                  }}
                />
              </picture>
            </Box>
            {/* Video */}
            {!videoBlocked && (
              <Box
                data-testid="video-background"
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="105vh"
                style={{
                  opacity: videoReady ? 1 : 0,
                  transition: "opacity 0.4s ease",
                  // Keep the video in its own composited layer so the
                  // gradients/backdrop above it cannot invalidate it on
                  // every paint while HLS segments stream in.
                  willChange: "opacity",
                  isolation: "isolate",
                  transform: "translateZ(0)",
                }}
              >
                <VideoPlayer
                  fullBackground
                  stream={{
                    type: "hls",
                    data: isMobile === true ? videoMobileURL : videoURL,
                  }}
                  metadata={{
                    video_title: formatMessage({
                      id: "LBL_EXTERIOR_META_TITLE",
                    }),
                    video_id:
                      isMobile === true ? "home-mobile-video" : "home-video",
                  }}
                  type="inline"
                  options={{
                    autoplay: true,
                    muted: true,
                    controls: false,
                    loop: true,
                    preload: "auto",
                  }}
                  onAutoplayFailed={() => setVideoBlocked(true)}
                  playingCallback={() => setVideoReady(true)}
                />
              </Box>
            )}
            {/* MASK GRADIENTS */}
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="120vh"
              bg={[
                "linear-gradient(to bottom, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0.5) 100%)",
                null,
                "linear-gradient(to left, rgba(0, 0, 0, 0) 62%, rgba(0, 0, 0, 0.5) 100%)",
              ]}
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="120vh"
              bg={
                "linear-gradient(to bottom, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.5) 100%)"
              }
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="120vh"
              bg="rgba(0, 0, 0, 0.10)"
            />
            {/* --- */}
          </Box>
        </Box>
        <Box width="100%" height="100%" px={[0, null, 20]} position="relative">
          <Container
            px={0}
            position="relative"
            height="100%"
            maxWidth="xl"
            display={["block", null, "flex"]}
            pt={0}
            pb={0}
            alignItems="flex-end"
          >
            <Box
              display={["none", "none", "flex"]}
              alignItems="center"
              h="100%"
            >
              <HeroContent
                animationId={animationId}
                opacity={opacityContent}
                imInterestedClick={imInterestedClick}
              />
            </Box>
            <Box
              maxWidth="xl"
              width="full"
              bottom={0}
              mb={[0, 0, 12]}
              height={["120vh", null, "0vh"]}
              display={["flex", "flex", "none"]}
            >
              <Box display={["flex", "flex", "none"]} h="90vh">
                <HeroContent
                  animationId={animationId}
                  opacity={opacityContent}
                  y={yContent}
                />
              </Box>
            </Box>

            {!noSpecs && (
              <Grid
                p={4}
                w="100%"
                maxW="1200px"
                templateColumns={[
                  "repeat(4, 1fr)",
                  null,
                  "repeat(6, 1fr)",
                  "repeat(5, 1fr)",
                ]}
                gap={["md", "md", 4, 6]}
                align={["center", null, "baseline"]}
                style={{ y: yContent }}
                pb={[0, null, "12vh"]}
                mx="auto"
              >
                <Item
                  value={<Trans id="LBL_TECHSPEC_VALUE_2" />}
                  label={<Trans id="LBL_TECHSPEC_LABEL_2" />}
                  delay={0.7}
                />
                <Item
                  value={<Trans id="LBL_TECHSPEC_VALUE_1" />}
                  label={<Trans id="LBL_TECHSPEC_LABEL_1" />}
                  delay={0.8}
                />
                <Item
                  value={<Trans id="LBL_TECHSPEC_VALUE_4" />}
                  label={<Trans id="LBL_TECHSPEC_LABEL_4" />}
                  delay={0.9}
                />
                <Item colSpan={1} display={["none", "none", "block", "none"]} />
                <Item
                  value={<Trans id="LBL_TECHSPEC_VALUE_3" />}
                  label={<Trans id="LBL_TECHSPEC_LABEL_3" />}
                  delay={1.0}
                />
                <Item colSpan={[4, 4, 2, 1]} delay={1.1}>
                  <CampaignPriceBlock model="hx50" compact variant="hero" />
                </Item>
                <Item colSpan={1} display={["none", "none", "block", "none"]} />
              </Grid>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

const HeroContent = ({ display, opacity, y, imInterestedClick }) => (
  <Flex
    display={display}
    px={[3, null, 0]}
    direction="column"
    mt={[0, 0, "30%", "30%", "30%", "36%"]}
    justify={["flex-end", "flex-end", "flex-start"]}
    position={["none", "none", "fixed"]}
    width="100vw"
    height={["none", "none", "100%"]}
    mx="auto"
    style={{ opacity, y }}
  >
    <Text
      type="title_lg"
      color="primary"
      initial="hidden"
      textAlign="left"
      mx={[2, 6, 0]}
      variants={appearBelow(0.1)}
      animate="visible"
    >
      HILL HX50
    </Text>
    <Text
      type="headline_lg"
      className="hero-headline-lcp"
      textAlign="left"
      mx={[2, 6, 0]}
      maxWidth={["100%", null, "70vw"]}
    >
      <Trans id="LBL_HEADER_TITLE_B" />
    </Text>
    <Flex
      mt={[4, null, "md"]}
      direction={["column", null, "row"]}
      justifyContent="flex-start"
      display={["block", "block", "flex"]}
      w={["100%", null, "auto"]}
    >
      <Box
        initial="hidden"
        variants={appearBelow(0.3)}
        animate="visible"
        mx={[2, 6, 0]}
      >
        <Button
          w={["100%", "100%", "180px"]}
          onClick={() =>
            document
              .getElementById("presentation")
              .scrollIntoView({ behavior: "smooth" })
          }
          trackLabel="discover_the_hx50"
          mr={[0, 4, 6]}
          mb={[2, 4, 6]}
          variant="outline"
          size="regular"
        >
          <Trans id="LBL_CTA_DISCOVER_B" />
        </Button>
      </Box>
      <Box
        initial="hidden"
        variants={appearBelow(0.5)}
        animate="visible"
        mx={[2, 6, 0]}
        align="center"
      >
        <Button
          w={["100%", "100%", "220px"]}
          onClick={
            imInterestedClick ||
            (() =>
              document
                .getElementById("register")
                .scrollIntoView({ behavior: "smooth" }))
          }
          trackLabel="im_interested"
          variant="solid"
          size="regular"
        >
          <Trans id="LBL_CTA_IMINTERESTED" />
        </Button>
      </Box>
    </Flex>
  </Flex>
);
